/**
 * @file index.tsx
 *
 * @fileoverview Displays a modal / dialog window on the screen.
 */
import { Content, Overlay, Portal, Root } from "@radix-ui/react-alert-dialog";
import { motion, MotionProps } from "framer-motion";
import React from "react";
import { withExitAnimation } from "../../../hocs/withExitAnimation";
import CloseIcon from "@jq-icons/Close";
import {
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../theme/spacing";
import styles from "./modal.module.scss";

// Define a new interface that extends MotionProps
type MotionDivProps = MotionProps & React.HTMLAttributes<HTMLDivElement>;
const CustomMotion = motion<MotionDivProps>("div");

type MotionButtonProps = MotionProps & React.HTMLAttributes<HTMLButtonElement>;
const CustomMotionButton = motion<MotionButtonProps>("button");

interface ModalProps extends SpaceProps {
  /**
   * Contents to render inside the modal.
   */
  children: React.ReactNode;
  /**
   * If the modal is currently visible.
   */
  isShown: boolean;
  /**
   * Callback for when hidding. Called for example when user hits escape.
   */
  onHide: () => void;
  /**
   * If a close button should be automatically added.
   *
   * @default "true"
   */
  showClose?: boolean;
  /**
   * A specific maxWidth for the modal.
   */
  maxWidth?: string;
  /**
   * Hover text shown on close button.
   *
   * @default "Close"
   */
  closeTitle?: string;
  /**
   * Unique identifier for the modal.
   */
  id?: string;
  /**
   * Additional styles for the modal to override default styling.
   */
  className?: string;
  /**
   * Define if ESC key can be used to close modal.
   *
   * @default "false"
   */
  disableEscapeKeyClose?: boolean
}

/**
 * A modal is used to show information on a overlay box.
 */
export default function Modal({
  children,
  isShown,
  onHide,
  showClose = true,
  id = "modal_popup",
  closeTitle = "Close",
  maxWidth = "750px",
  className,
  disableEscapeKeyClose = false,
  ...props
}: ModalProps) {
  return withExitAnimation(
    <Root open={isShown} onOpenChange={(open) => !open && onHide()}>
      <Portal>
        <Overlay className={styles.modalOverlay} />
        <CustomMotion
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{
            ease: "anticipate",
            duration: 0.15,
          }}
          className={`
            ${styles.modalContent}
            ${generateMarginClasses(props, styles)}
            ${generatePaddingClasses(props, styles)}
            ${className}
          `.trim()}
          style={{
            maxWidth,
          }}
        >
          <Content
            aria-label="Modal content"
            {...(disableEscapeKeyClose && {
              onEscapeKeyDown: (event) => {
                event.preventDefault();
              },
            })}
          >
            <div
              className={`${styles.contentWrapper} jcl-modal-content-wrapper`}
            >
              {children}
            </div>

            {showClose && (
              <CustomMotionButton
                className={styles.modalCloseBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onHide();
                }}
                whileFocus={{ scale: 1.2 }}
                whileHover={{ scale: 1.2 }}
              >
                <CloseIcon title={closeTitle} />
              </CustomMotionButton>
            )}
          </Content>
        </CustomMotion>
      </Portal>
    </Root>,
    isShown,
  );
}

export { Modal };
